'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, X } from '@phosphor-icons/react';
import { MapPinIcon, SpinnerIcon } from '@/components/icons';
import { deliveryApi, type City } from '@/lib/api/delivery';
import { useAddressStore } from '@/stores/address-store';
import { useMoneyRules } from '@/lib/money-rules';
import { OPEN_HOUR, CLOSE_HOUR } from '@/lib/business-hours';
import { DeliveryAddressForm, type CheckedMapAddress } from './DeliveryAddressForm';
import { DeliveryJourney } from './DeliveryJourney';
import { DeliveryHelp } from './DeliveryHelp';
import { DeliveryTariffPicker } from './DeliveryTariffPicker';
import { deliveryMoney, deliveryTariffs } from './delivery-model';
import './delivery-experience.css';

const DeliveryMap = dynamic(() => import('./YandexMapInner'), {
  ssr: false,
  loading: () => (
    <div className="delivery-map-loading" role="status">
      <SpinnerIcon size={25} className="delivery-spinner" /> Загружаем карту…
    </div>
  ),
});

export function DeliveryExperience({ phone }: { phone: string }) {
  const selectedCity = useAddressStore((state) => state.selectedCity);
  const hydrated = useAddressStore((state) => state._hasHydrated);
  const setSelectedCity = useAddressStore((state) => state.setSelectedCity);
  const [selectedTariff, setSelectedTariff] = useState<string | null>(null);
  const [addressMarker, setAddressMarker] = useState<CheckedMapAddress>();
  const freeDelivery = useMoneyRules().free_delivery;
  const citiesQuery = useQuery({
    queryKey: ['delivery-page-cities'],
    queryFn: deliveryApi.getCities,
    staleTime: 300000,
    retry: 1,
  });
  const cities = citiesQuery.data ?? [];
  const city =
    cities.find((item) => item.id === (hydrated ? selectedCity?.id : undefined)) ?? cities[0];
  useEffect(() => {
    if (hydrated && city && city.id !== selectedCity?.id) setSelectedCity(city);
  }, [hydrated, city, selectedCity?.id, setSelectedCity]);
  const pointsQuery = useQuery({
    queryKey: ['delivery-page-points', city?.id],
    queryFn: () => deliveryApi.getDeliveryPoints(city!.id),
    enabled: !!city,
    staleTime: 60000,
    retry: 1,
  });
  const points = pointsQuery.data ?? [];
  const tariffs = useMemo(() => deliveryTariffs(pointsQuery.data ?? []), [pointsQuery.data]);
  const tariff = tariffs.find((item) => item.key === selectedTariff);
  const highlightedZoneIds = tariff?.zoneIds;
  useEffect(
    () =>
      useAddressStore.subscribe((state, previous) => {
        if (state.selectedCity?.id !== previous.selectedCity?.id) {
          setSelectedTariff(null);
          setAddressMarker(undefined);
        }
      }),
    []
  );
  const openingHours = `${String(OPEN_HOUR).padStart(2, '0')}:00–${String(CLOSE_HOUR).padStart(2, '0')}:00`;

  const changeCity = (next: City) => {
    setSelectedCity(next);
    setSelectedTariff(null);
    setAddressMarker(undefined);
  };

  return (
    <main className="delivery-page" id="delivery">
      <div className="delivery-container">
        <section className="delivery-intro" aria-labelledby="delivery-title">
          <div className="delivery-intro-copy">
            <Link className="delivery-back-link" href="/">
              Главная <span>/ Доставка</span>
            </Link>
            <h1 id="delivery-title">Доставка</h1>
            <p>
              Ваши любимые роллы.
              <br className="delivery-mobile-break" /> По вашему адресу.
            </p>
            <span className="delivery-mobile-hours">Ежедневно {openingHours}</span>
          </div>
          <div className="delivery-intro-aside">
            <div className="delivery-hours">
              <span>Принимаем заказы</span>
              <strong>{openingHours}</strong>
              <span>Каждый день</span>
            </div>
            <div className="delivery-mascot">
              <img
                src="/storefront/offers-mascot-invite-v1.webp"
                width={768}
                height={1024}
                alt=""
                fetchPriority="high"
              />
            </div>
          </div>
        </section>

        <section className="delivery-workspace" aria-label="Проверка адреса и карта доставки">
          <DeliveryAddressForm
            key={city?.id ?? 'loading'}
            cities={cities}
            city={city}
            citiesLoading={citiesQuery.isPending}
            citiesError={citiesQuery.isError}
            onCityChange={changeCity}
            onRetryCities={() => void citiesQuery.refetch()}
            onChecked={(zoneId, marker) => {
              setAddressMarker(marker);
              setSelectedTariff(
                zoneId ? (tariffs.find((item) => item.zoneIds.includes(zoneId))?.key ?? null) : null
              );
            }}
          />
          <div className="delivery-map-area" id="delivery-map">
            <div className="delivery-map-topline">
              <h2>
                <MapPinIcon size={17} aria-hidden="true" /> Зоны доставки
              </h2>
              <span>{city?.name ?? 'Карта'}</span>
            </div>
            <div className="delivery-map-canvas">
              <DeliveryMap
                deliveryPoints={points}
                highlightedZoneIds={highlightedZoneIds}
                addressMarker={addressMarker}
                onZoneSelect={(id) => {
                  setSelectedTariff(tariffs.find((item) => item.zoneIds.includes(id))?.key ?? null);
                }}
              />
              {(pointsQuery.isError || (!pointsQuery.isPending && !tariffs.length)) && (
                <div className="delivery-map-data-note" role="status">
                  {pointsQuery.isError ? (
                    <>
                      Зоны не загрузились.{' '}
                      <button onClick={() => void pointsQuery.refetch()}>Повторить</button>
                    </>
                  ) : (
                    'Для этого города пока нет доступных зон.'
                  )}
                </div>
              )}
              {pointsQuery.isFetching && (
                <div className="delivery-map-data-note" role="status">
                  <SpinnerIcon size={16} className="delivery-spinner" /> Обновляем зоны…
                </div>
              )}
            </div>
            <div className="delivery-map-legend">
              {tariff ? (
                <>
                  <span className="delivery-map-selection">
                    Доставка {tariff.cost === 0 ? 'бесплатно' : deliveryMoney(tariff.cost)}{' '}
                    <span>
                      · от {deliveryMoney(tariff.minimum)} · {tariff.time}
                    </span>
                  </span>
                  <button
                    type="button"
                    aria-label="Показать все зоны"
                    onClick={() => {
                      setSelectedTariff(null);
                      setAddressMarker(undefined);
                    }}
                  >
                    <X size={18} />
                  </button>
                </>
              ) : (
                <>
                  <span>
                    <i className="is-free" /> Бесплатная
                  </span>
                  <span>
                    <i className="is-paid" /> Платная
                  </span>
                  <a href="#delivery-rates">
                    Все тарифы <ArrowDown size={14} />
                  </a>
                </>
              )}
            </div>
          </div>
        </section>

        <DeliveryTariffPicker
          tariffs={tariffs}
          selectedKey={selectedTariff}
          onSelect={(key) => {
            setSelectedTariff(key);
            setAddressMarker(undefined);
          }}
          loading={
            citiesQuery.isPending || (pointsQuery.isPending && !!city && !citiesQuery.isError)
          }
          error={pointsQuery.isError || citiesQuery.isError}
          onRetry={() => {
            if (citiesQuery.isError) void citiesQuery.refetch();
            else void pointsQuery.refetch();
          }}
          freeDelivery={freeDelivery}
        />

        <DeliveryJourney />

        <DeliveryHelp phone={phone} />
      </div>
    </main>
  );
}
